import type { ID, ISODateString } from "./common";

export const TICKET_CATEGORIES = [
  "missing_item",
  "quality",
  "late_delivery",
  "payment",
  "plan_change",
  "other",
] as const;
export type TicketCategory = (typeof TICKET_CATEGORIES)[number];

export const TICKET_CATEGORY_LABELS: Record<TicketCategory, string> = {
  missing_item: "Missing item",
  quality: "Quality",
  late_delivery: "Late delivery",
  payment: "Payment",
  plan_change: "Plan change",
  other: "Other",
};

export const TICKET_PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];

export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  low: "Low",
  normal: "Normal",
  high: "High",
  urgent: "Urgent",
};

export const TICKET_STATUSES = ["open", "pending", "resolved", "closed"] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  open: "Open",
  pending: "Waiting on customer",
  resolved: "Resolved",
  closed: "Closed",
};

/** Who wrote a message on a ticket. */
export const TICKET_AUTHORS = ["customer", "agent", "system"] as const;
export type TicketAuthor = (typeof TICKET_AUTHORS)[number];

/** How the customer reached us. */
export const TICKET_CHANNELS = ["whatsapp", "call", "app", "email"] as const;
export type TicketChannel = (typeof TICKET_CHANNELS)[number];

export const TICKET_CHANNEL_LABELS: Record<TicketChannel, string> = {
  whatsapp: "WhatsApp",
  call: "Phone call",
  app: "App",
  email: "Email",
};

export interface TicketMessage {
  id: ID;
  author: TicketAuthor;
  name: string;
  body: string;
  /** Internal notes are never shown to the customer. */
  internal: boolean;
  at: ISODateString;
}

export interface Ticket {
  id: ID;
  number: string;
  subject: string;
  customerId: ID;
  customerName: string;
  customerPhone: string;
  orderId: ID | null;
  orderNumber: string | null;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  channel: TicketChannel;
  assignee: string | null;
  messages: TicketMessage[];
  createdAt: ISODateString;
  updatedAt: ISODateString;
  /** First-response / resolution deadline. */
  dueAt: ISODateString;
}

/** The help-centre sections answers are filed under; the storefront shows a tile for each. */
export const HELP_TOPICS = ["deliveries", "refunds", "plans", "flowers"] as const;
export type HelpTopic = (typeof HELP_TOPICS)[number];

export const HELP_TOPIC_LABELS: Record<HelpTopic, string> = {
  deliveries: "Deliveries & slots",
  refunds: "Refunds & wallet",
  plans: "Milk plans & pauses",
  flowers: "Flowers & fruit orders",
};

/** A question and answer in the storefront help centre, written by staff in the admin. */
export interface HelpFaq {
  id: ID;
  topic: HelpTopic;
  question: string;
  answer: string;
  /** Extra words people search with that aren't in the question or answer, lower case. */
  keywords: string[];
  /** Unpublished answers are drafts: only staff see them. */
  published: boolean;
  /** Position within its topic, from 1. */
  sortOrder: number;
  updatedAt: ISODateString;
}

export interface HelpFaqQuery {
  /** Every word must appear in the question, answer, topic or search words. */
  q?: string;
  topic?: HelpTopic;
}

/** The help centre's answers for a search and topic, with what the topic tiles and chips show. */
export interface HelpCentre {
  items: HelpFaq[];
  /** Published answers per topic (all of them, whatever the search); topics with none left out. */
  topics: { topic: HelpTopic; count: number }[];
  /** Search chips: one search word per topic, so every chip finds something. */
  popularSearches: string[];
}
